import React, { useMemo, useState } from 'react';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import { Button, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { map } from 'lodash';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import appConfig from '../../../config/appConfig';
import { completePostLoginFlow } from '../../../utils/authToken';

export const createLoginEmailSchema = (t) =>
  Yup.object().shape({
    email: Yup.string().email(t.invalid_email_format).required(t.email_required),
    password: Yup.string().required(t.password_required),
  });

const LoginForm = ({ redirectTo }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login: l, snackbarMessages, language } = useTranslatedMessages();
  const authSnacks = snackbarMessages.auth ?? {};
  const validationText = l.validations ?? {};

  const schema = useMemo(
    () =>
      createLoginEmailSchema({
        invalid_email_format: validationText.invalid_email_format ?? 'Invalid email format',
        email_required: validationText.email_required ?? 'Email is required',
        password_required: validationText.password_required ?? 'Password is required',
      }),
    [validationText],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const onSubmit = async (data) => {
    setLoading(true);

    authApiClient
      .post('/login', data)
      .then(async (res) => {
        if (!res.data.token) {
          dispatch(
            showSnackbar({
              message:
                res.data.message ||
                authSnacks.otp_sent_email ||
                l.snackbar?.otp_sent_success ||
                'OTP sent to your email',
              severity: 'success',
            }),
          );

          return;
        }

        const result = await completePostLoginFlow({
          requiresOrgSelection: !!res.data.requires_org_selection,
          email: data.email,
          dispatch,
          navigate,
          redirectTo,
          queryClient,
          onMfaRequired: (payload) => {
            navigate('/login', {
              replace: true,
              state: {
                stage: 'otp_required',
                email: data.email,
                message: payload.message,
                orgMfa: true,
                organizationId: payload.organizationId,
                redirectTo,
              },
            });
          },
        });

        if (result?.error) {
          const message =
            result.error?.response?.data?.errors?.message ||
            result.error?.response?.data?.message ||
            result.error?.message ||
            l.snackbar?.something_went_wrong ||
            'Something went wrong!';

          dispatch(
            showSnackbar({
              message,
              severity: 'error',
            }),
          );
        }
      })
      .catch((err) => {
        if (err?.response?.data?.errors?.message) {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.errors?.message,
              severity: 'error',
            }),
          );
        } else if (err?.response?.data?.errors) {
          map(err?.response?.data?.errors, (value, key) => {
            setError(key, { message: value, type: 'string' });
          });
        } else {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || l.snackbar?.something_went_wrong || 'Something went wrong!',
              severity: 'warning',
            }),
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>
      <Stack spacing={2}>
        <CustomTextInput
          data-testid="email-input"
          name="email"
          label={l.form?.email_label ?? 'Email'}
          control={control}
          errors={errors}
        />
        <CustomTextInput
          data-testid="password-input"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={l.form?.password_label ?? 'Password'}
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Typography variant="body2">
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            {l.links?.forgot_password ?? 'Forgot Password?'}
          </Link>
        </Typography>
        <Button
          data-testid="sign-in-button"
          type="button"
          variant="contained"
          className="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {loading ? (l.actions?.logging_in ?? 'Logging in...') : (l.actions?.sign_in ?? 'Sign In')}
        </Button>
        <Typography variant="body2">
          <Link to="/signup" style={{ textDecoration: 'none' }}>
            {l.links?.signup_prompt ?? "Don't have an account? Sign Up"}
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default LoginForm;
